/**
 * How big a PNG export can be.
 *
 * The decision part of rasterising, kept apart from any rasteriser. The browser draws the export
 * SVG onto a `<canvas>`; the phone draws it with `react-native-svg`'s native snapshot. Both have
 * to answer the same question first: at the scale asked for, does the image fit what this
 * platform can allocate? If not, reduce it, and *say so*.
 *
 * This file has no DOM, which is why it is on the package root and not behind `./browser`.
 */
export const PNG_MIME_TYPE = 'image/png';
/**
 * The largest edge a browser canvas will accept.
 *
 * Browsers cap both a single dimension and the total area, and the numbers differ between them;
 * Safari is the strictest. Exceeding the cap does not throw. The canvas comes back blank, and the
 * user gets an empty PNG with no explanation. Clamping and *saying so* is the only outcome that
 * is not a silent failure.
 */
export const MAX_RASTER_EDGE = 8192;
/**
 * Pixels per unit. Two by default, because one is soft on every display made this decade and
 * the reason to reach for a PNG at all is usually to paste it somewhere.
 */
export const DEFAULT_RASTER_SCALE = 2;
/**
 * The pixel dimensions for a scale, reduced if the platform could not hold them.
 *
 * @param maxEdge  The longest edge this platform will draw. The browser's canvas limit by
 *                 default; a phone passes a smaller one, because a bitmap is held in memory whole
 *                 and 8192 × 8192 is a quarter of a gigabyte.
 */
export function rasterSizeFor(width, height, scale, maxEdge = MAX_RASTER_EDGE) {
    const wanted = Math.max(0.1, scale);
    const longest = Math.max(width, height) * wanted;
    if (longest <= maxEdge) {
        return {
            width: Math.max(1, Math.round(width * wanted)),
            height: Math.max(1, Math.round(height * wanted)),
            scale: wanted,
            clamped: false,
        };
    }
    const reduced = maxEdge / Math.max(width, height);
    return {
        width: Math.max(1, Math.round(width * reduced)),
        height: Math.max(1, Math.round(height * reduced)),
        scale: reduced,
        clamped: true,
    };
}
//# sourceMappingURL=raster-size.js.map